import { createApiRef, FetchApi } from '@backstage/core-plugin-api';
import {
  UserInfo,
  VirtualKey,
  ModelInfo,
  UsageMetrics,
  TeamInfo,
  GenerateKeyRequest,
  GenerateKeyResponse,
  UpdateKeyRequest,
  AuditLogsParams,
  PaginatedAuditLogs,
  LiteLlmConfig,
  VectorStoreInfo,
  McpServerInfo,
  CreateTeamRequest,
  CreateTeamResponse,
  UpdateTeamRequest,
} from './types';

class ApiError extends Error {
  body: unknown;
  status: number;
  constructor(message: string, status: number, body: unknown) {
    super(message);
    this.status = status;
    this.body = body;
  }
}

export interface LiteLlmApiInterface {
  getUserInfo(): Promise<UserInfo>;
  listKeys(): Promise<VirtualKey[]>;
  generateKey(request: GenerateKeyRequest): Promise<GenerateKeyResponse>;
  updateKey(keyId: string, request: UpdateKeyRequest): Promise<VirtualKey>;
  deleteKey(keyId: string): Promise<{ success: boolean }>;
  blockKey(keyId: string): Promise<void>;
  unblockKey(keyId: string): Promise<void>;
  resetKeySpend(keyId: string): Promise<void>;
  pruneExpiredKeys(): Promise<{ pruned: number }>;
  listModels(): Promise<ModelInfo[]>;
  getTeams(): Promise<TeamInfo[]>;
  getManagedTeams(): Promise<TeamInfo[]>;
  addTeamMember(
    teamId: string,
    body: { userEntityRef: string; maxBudgetInTeam?: number },
  ): Promise<TeamInfo>;
  removeTeamMember(teamId: string, userEntityRef: string): Promise<TeamInfo>;
  getVectorStores(): Promise<VectorStoreInfo[]>;
  setTeamKnowledgeBases(teamId: string, vectorStores: string[]): Promise<TeamInfo>;
  getMcpServers(): Promise<McpServerInfo[]>;
  setTeamMcpServers(teamId: string, mcpServers: string[]): Promise<TeamInfo>;
  getUsage(startDate: string, endDate: string): Promise<UsageMetrics>;
  getTeamUsage(teamId: string, startDate: string, endDate: string): Promise<UsageMetrics>;
  getAuditLogs(params: AuditLogsParams): Promise<PaginatedAuditLogs>;
  getConfig(): Promise<LiteLlmConfig>;
  createTeam(request: CreateTeamRequest): Promise<CreateTeamResponse>;
  updateTeam(teamId: string, request: UpdateTeamRequest): Promise<TeamInfo>;
}

export const liteLlmApiRef = createApiRef<LiteLlmApiInterface>({
  id: 'plugin.litellm.api',
});

export type ExpiryStatus = 'expired' | 'soon' | 'ok';

export function expiryStatus(expiresAt?: string): ExpiryStatus | null {
  if (!expiresAt) return null;
  const diff = new Date(expiresAt).getTime() - Date.now();
  const days = diff / (1000 * 60 * 60 * 24);
  if (days < 0) return 'expired';
  if (days < 7) return 'soon';
  return 'ok';
}

export class LiteLlmApi implements LiteLlmApiInterface {
  private fetchApi: FetchApi;
  private basePath: string;

  constructor(fetchApi: FetchApi, basePath: string = '/api/litellm') {
    this.fetchApi = fetchApi;
    this.basePath = basePath;
  }

  private async throwIfNotOk(response: Response): Promise<void> {
    if (!response.ok) {
      let body: unknown;
      try { body = await response.json(); } catch { body = await response.text().catch(() => ''); }
      const message =
        (typeof body === 'object' && body && typeof (body as any).error === 'string'
          ? (body as any).error
          : undefined) ?? `${response.status} ${response.statusText}`;
      throw new ApiError(message, response.status, body);
    }
  }

  private async get<T>(path: string, params?: Record<string, string>): Promise<T> {
    const url = new URL(`${this.basePath}${path}`, window.location.origin);
    if (params) {
      Object.entries(params).forEach(([key, value]) => url.searchParams.append(key, value));
    }
    const response = await this.fetchApi.fetch(url.toString());
    await this.throwIfNotOk(response);
    return response.json();
  }

  private async post<T>(path: string, body: unknown): Promise<T> {
    const response = await this.fetchApi.fetch(`${this.basePath}${path}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    await this.throwIfNotOk(response);
    return response.json();
  }

  private async del<T>(path: string): Promise<T> {
    const response = await this.fetchApi.fetch(`${this.basePath}${path}`, {
      method: 'DELETE',
      headers: { 'Content-Type': 'application/json' },
    });
    await this.throwIfNotOk(response);
    return response.json();
  }

  private async patch<T>(path: string, body: unknown): Promise<T> {
    const response = await this.fetchApi.fetch(`${this.basePath}${path}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    await this.throwIfNotOk(response);
    return response.json();
  }

  private async put<T>(path: string, body: unknown): Promise<T> {
    const response = await this.fetchApi.fetch(`${this.basePath}${path}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    await this.throwIfNotOk(response);
    return response.json();
  }

  // User identity is resolved server-side from the Backstage Bearer token.
  // No user_id param needed on the frontend.
  async getUserInfo(): Promise<UserInfo> {
    return this.get<UserInfo>('/user/info');
  }

  async listKeys(): Promise<VirtualKey[]> {
    return this.get<VirtualKey[]>('/keys');
  }

  async generateKey(request: GenerateKeyRequest): Promise<GenerateKeyResponse> {
    return this.post<GenerateKeyResponse>('/keys/generate', request);
  }

  async updateKey(keyId: string, request: UpdateKeyRequest): Promise<VirtualKey> {
    return this.post<VirtualKey>(`/keys/${encodeURIComponent(keyId)}/update`, request);
  }

  async deleteKey(keyId: string): Promise<{ success: boolean }> {
    return this.del<{ success: boolean }>(`/keys/${encodeURIComponent(keyId)}`);
  }

  async blockKey(keyId: string): Promise<void> {
    await this.post(`/keys/${encodeURIComponent(keyId)}/block`, {});
  }

  async unblockKey(keyId: string): Promise<void> {
    await this.post(`/keys/${encodeURIComponent(keyId)}/unblock`, {});
  }

  async resetKeySpend(keyId: string): Promise<void> {
    await this.post(`/keys/${encodeURIComponent(keyId)}/reset_spend`, {});
  }

  async pruneExpiredKeys(): Promise<{ pruned: number }> {
    const keys = await this.get<VirtualKey[]>('/keys');
    const expired = keys.filter(k => expiryStatus(k.expires_at) === 'expired');
    if (expired.length === 0) return { pruned: 0 };
    for (const key of expired) {
      try {
        await this.del(`/keys/${encodeURIComponent(key.token ?? key.key)}`);
      } catch (err) {
        // eslint-disable-next-line no-console -- best-effort bulk cleanup; no errorApi in this client class to report through
        console.warn(`Failed to delete expired key ${key.token ?? key.key}:`, err);
      }
    }
    return { pruned: expired.length };
  }

  async getAuditLogs(params: AuditLogsParams): Promise<PaginatedAuditLogs> {
    const strParams: Record<string, string> = {};
    if (params.page !== undefined) strParams.page = String(params.page);
    if (params.page_size !== undefined) strParams.page_size = String(params.page_size);
    if (params.start_date) strParams.start_date = params.start_date;
    if (params.end_date) strParams.end_date = params.end_date;
    if (params.action) strParams.action = params.action;
    if (params.table_name) strParams.table_name = params.table_name;
    if (params.changed_by) strParams.changed_by = params.changed_by;
    return this.get<PaginatedAuditLogs>('/audit', strParams);
  }

  async listModels(): Promise<ModelInfo[]> {
    return this.get<ModelInfo[]>('/models');
  }

  async getTeams(): Promise<TeamInfo[]> {
    return this.get<TeamInfo[]>('/teams');
  }

  // Teams whose owning_group the caller administers (server-scoped). Distinct
  // from getTeams(), which returns only the teams the caller is a member of.
  async getManagedTeams(): Promise<TeamInfo[]> {
    return this.get<TeamInfo[]>('/teams/managed');
  }

  async addTeamMember(
    teamId: string,
    body: { userEntityRef: string; maxBudgetInTeam?: number },
  ): Promise<TeamInfo> {
    return this.post<TeamInfo>(
      `/teams/${encodeURIComponent(teamId)}/members`,
      body,
    );
  }

  async removeTeamMember(
    teamId: string,
    userEntityRef: string,
  ): Promise<TeamInfo> {
    return this.del<TeamInfo>(
      `/teams/${encodeURIComponent(teamId)}/members?userEntityRef=${encodeURIComponent(
        userEntityRef,
      )}`,
    );
  }

  // Vector stores (knowledge bases) the caller may attach — already filtered
  // server-side to the operator's allowlist.
  async getVectorStores(): Promise<VectorStoreInfo[]> {
    return this.get<VectorStoreInfo[]>('/vector-stores');
  }

  async setTeamKnowledgeBases(
    teamId: string,
    vectorStores: string[],
  ): Promise<TeamInfo> {
    return this.put<TeamInfo>(
      `/teams/${encodeURIComponent(teamId)}/knowledge-bases`,
      { vector_stores: vectorStores },
    );
  }

  async getMcpServers(): Promise<McpServerInfo[]> {
    return this.get<McpServerInfo[]>('/mcp-servers');
  }

  async setTeamMcpServers(
    teamId: string,
    mcpServers: string[],
  ): Promise<TeamInfo> {
    return this.put<TeamInfo>(
      `/teams/${encodeURIComponent(teamId)}/mcp-servers`,
      { mcp_servers: mcpServers },
    );
  }

  async getUsage(startDate: string, endDate: string): Promise<UsageMetrics> {
    return this.get<UsageMetrics>('/usage', { start_date: startDate, end_date: endDate });
  }

  async getTeamUsage(teamId: string, startDate: string, endDate: string): Promise<UsageMetrics> {
    return this.get<UsageMetrics>(`/teams/${encodeURIComponent(teamId)}/usage`, {
      start_date: startDate,
      end_date: endDate,
    });
  }

  async getConfig(): Promise<LiteLlmConfig> {
    return this.get<LiteLlmConfig>('/config');
  }

  async createTeam(request: CreateTeamRequest): Promise<CreateTeamResponse> {
    return this.post<CreateTeamResponse>('/teams', request);
  }

  async updateTeam(teamId: string, request: UpdateTeamRequest): Promise<TeamInfo> {
    return this.patch<TeamInfo>(`/teams/${encodeURIComponent(teamId)}`, request);
  }
}
